import { useEffect } from 'react';

interface UseKeyboardShortcutOptions {
  onEnter?: () => void;
  onEscape?: () => void;
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Custom hook for handling keyboard shortcuts (Enter and Escape keys)
 * Commonly used for save/cancel operations in modals and edit modes
 *
 * @param options Configuration object
 * @param options.onEnter Callback when Enter is pressed
 * @param options.onEscape Callback when Escape is pressed
 * @param options.enabled Whether the shortcuts are active (default: true)
 * @param options.preventDefault Whether to prevent default behavior (default: true)
 */
export function useKeyboardShortcut({
  onEnter,
  onEscape,
  enabled = true,
  preventDefault = true,
}: UseKeyboardShortcutOptions) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && onEnter) {
        if (preventDefault) e.preventDefault();
        onEnter();
      } else if (e.key === 'Escape' && onEscape) {
        if (preventDefault) e.preventDefault();
        onEscape();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onEnter, onEscape, enabled, preventDefault]);
}
